import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dateInput", "hiddenInput", "overlay", "monthSelect", "yearSelect", "grid", "calendarIcon", "fieldWrapper", "fieldLabel", "errorText"]

  connect() {
    this.selectedDate = null
    this.viewMonth = new Date().getMonth()
    this.viewYear = new Date().getFullYear()
    this.touched = false
    this.boundClose = this.closeOnOutsideClick.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
  }

  handleInput(event) {
    const value = this.dateInputTarget.value
    const digits = value.replace(/\D/g, "")
    let formatted = this.formatDigits(digits)

    const isDeleting = event.inputType && event.inputType.startsWith("delete")
    if (!isDeleting && (digits.length === 2 || digits.length === 4) && value.length > formatted.length) {
      formatted += " / "
    }

    this.dateInputTarget.value = formatted
    this.syncFromInput(digits)
    this.validate(digits)
  }

  formatDigits(digits) {
    if (digits.length <= 2) return digits
    if (digits.length <= 4) return `${digits.slice(0, 2)} / ${digits.slice(2)}`
    return `${digits.slice(0, 2)} / ${digits.slice(2, 4)} / ${digits.slice(4, 8)}`
  }

  handleBlur() {
    this.touched = true
    let digits = this.dateInputTarget.value.replace(/\D/g, "")
    if (digits.length === 6) {
      const yy = parseInt(digits.slice(4, 6), 10)
      const currentYear = new Date().getFullYear()
      const currentCentury = Math.floor(currentYear / 100) * 100
      const century = yy <= currentYear % 100 ? currentCentury : currentCentury - 100
      digits = digits.slice(0, 4) + String(century + yy)
      this.dateInputTarget.value = this.formatDigits(digits)
      this.syncFromInput(digits)
    }
    this.validate(digits)
  }

  syncFromInput(digits) {
    if (digits.length < 8) {
      this.hiddenInputTarget.value = ""
      this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
      return
    }

    const month = parseInt(digits.slice(0, 2), 10)
    const day = parseInt(digits.slice(2, 4), 10)
    const year = parseInt(digits.slice(4, 8), 10)

    if (this.isValidDate(month, day, year)) {
      this.selectedDate = new Date(year, month - 1, day)
      this.viewMonth = month - 1
      this.viewYear = year
      const m = String(month).padStart(2, "0")
      const d = String(day).padStart(2, "0")
      this.hiddenInputTarget.value = `${year}-${m}-${d}`
    } else {
      this.hiddenInputTarget.value = ""
      this.selectedDate = null
    }

    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  validate(digits) {
    if (digits.length === 0) {
      if (this.touched) {
        this.showError("Date of birth is required")
      } else {
        this.clearError()
      }
      return
    }

    if (digits.length >= 2) {
      const month = parseInt(digits.slice(0, 2), 10)
      if (month < 1 || month > 12) {
        this.showError("Invalid month")
        return
      }
    }

    if (digits.length >= 4) {
      const month = parseInt(digits.slice(0, 2), 10)
      const day = parseInt(digits.slice(2, 4), 10)
      if (day < 1 || day > 31) {
        this.showError("Invalid day")
        return
      }
      const daysInMonth = new Date(2000, month, 0).getDate()
      if (day > daysInMonth) {
        this.showError("Invalid day for this month")
        return
      }
    }

    if (digits.length >= 8) {
      const error = this.getDateError(digits)
      if (error) {
        this.showError(error)
        return
      }
    }

    if (this.touched && digits.length < 8) {
      this.showError("Please enter a complete date (MM / DD / YYYY)")
      return
    }

    this.clearError()
  }

  getDateError(digits) {
    const month = parseInt(digits.slice(0, 2), 10)
    const day = parseInt(digits.slice(2, 4), 10)
    const year = parseInt(digits.slice(4, 8), 10)
    const currentYear = new Date().getFullYear()

    if (month < 1 || month > 12) return "Invalid month"
    if (year < currentYear - 120 || year > currentYear) return "Invalid year"
    const daysInMonth = new Date(year, month, 0).getDate()
    if (day < 1 || day > daysInMonth) return "Invalid day for this month"

    const dob = new Date(year, month - 1, day)
    if (dob > new Date()) return "Date cannot be in the future"

    const now = new Date()
    const age = now.getFullYear() - dob.getFullYear()
    const monthDiff = now.getMonth() - dob.getMonth()
    const dayDiff = now.getDate() - dob.getDate()
    const fullAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age
    if (fullAge < 18) return "Must be at least 18 years old"

    return null
  }

  isValidDate(month, day, year) {
    return this.getDateError(
      String(month).padStart(2, "0") + String(day).padStart(2, "0") + String(year)
    ) === null
  }

  showError(message) {
    this.fieldWrapperTarget.style.borderColor = "rgb(239, 68, 68)"
    this.fieldLabelTarget.style.color = "rgb(239, 68, 68)"
    this.errorTextTarget.textContent = message
    this.errorTextTarget.classList.remove("hidden")
  }

  clearError() {
    this.fieldWrapperTarget.style.borderColor = ""
    this.fieldLabelTarget.style.color = ""
    this.errorTextTarget.textContent = ""
    this.errorTextTarget.classList.add("hidden")
  }

  open(event) {
    event.stopPropagation()
    this.overlayTarget.classList.remove("hidden")
    this.buildMonthOptions()
    this.buildYearOptions()
    this.renderGrid()
    document.addEventListener("click", this.boundClose)
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    document.removeEventListener("click", this.boundClose)
  }

  cancel(event) {
    event.stopPropagation()
    this.close()
  }

  confirm(event) {
    event.stopPropagation()
    if (!this.selectedDate) return

    const y = this.selectedDate.getFullYear()
    const m = String(this.selectedDate.getMonth() + 1).padStart(2, "0")
    const d = String(this.selectedDate.getDate()).padStart(2, "0")

    this.hiddenInputTarget.value = `${y}-${m}-${d}`
    this.dateInputTarget.value = `${m} / ${d} / ${y}`

    this.clearError()
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  changeMonth(event) {
    event.stopPropagation()
    this.viewMonth = parseInt(event.target.value, 10)
    this.renderGrid()
  }

  changeYear(event) {
    event.stopPropagation()
    this.viewYear = parseInt(event.target.value, 10)
    this.renderGrid()
  }

  selectDay(event) {
    event.stopPropagation()
    const day = parseInt(event.currentTarget.dataset.day, 10)
    this.selectedDate = new Date(this.viewYear, this.viewMonth, day)
    this.renderGrid()
  }

  closeOnOutsideClick(event) {
    if (!this.overlayTarget.contains(event.target) && !this.element.contains(event.target)) {
      this.close()
    }
  }

  buildMonthOptions() {
    const months = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"]
    this.monthSelectTarget.innerHTML = months
      .map((name, i) => `<option value="${i}" ${i === this.viewMonth ? "selected" : ""}>${name}</option>`)
      .join("")
  }

  buildYearOptions() {
    const currentYear = new Date().getFullYear()
    const options = []
    for (let y = currentYear; y >= currentYear - 120; y--) {
      options.push(`<option value="${y}" ${y === this.viewYear ? "selected" : ""}>${y}</option>`)
    }
    this.yearSelectTarget.innerHTML = options.join("")
  }

  renderGrid() {
    const firstDay = new Date(this.viewYear, this.viewMonth, 1).getDay()
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate()
    const daysInPrevMonth = new Date(this.viewYear, this.viewMonth, 0).getDate()

    let cells = ""

    for (let i = firstDay - 1; i >= 0; i--) {
      cells += `<div class="py-2 text-center text-sm opacity-30">${daysInPrevMonth - i}</div>`
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const isSelected = this.selectedDate &&
        this.selectedDate.getFullYear() === this.viewYear &&
        this.selectedDate.getMonth() === this.viewMonth &&
        this.selectedDate.getDate() === d

      const selectedClass = isSelected
        ? "wdark:bg-wdark-button-background wlight:bg-wlight-button-background dark:bg-dark-button-background wdark:text-wdark-button-text-and-icon wlight:text-wlight-button-text-and-icon dark:text-dark-button-text-and-icon rounded-full"
        : ""

      cells += `<button type="button" data-day="${d}" data-action="click->date-picker#selectDay"
        class="py-2 text-center text-sm cursor-pointer hover:opacity-75 ${selectedClass}">${d}</button>`
    }

    const totalCells = firstDay + daysInMonth
    const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7)
    for (let i = 1; i <= remaining; i++) {
      cells += `<div class="py-2 text-center text-sm opacity-30">${i}</div>`
    }

    this.gridTarget.innerHTML = cells
  }
}
